import { useEffect, useMemo, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"

import moment from "moment/moment"
import Delete from "pages/product-exclusivity/delete"
import Filter from "pages/product-exclusivity/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useProductUnitExclusivityRepositories from "repositories/product-exclusivity"
import useBranchRepositories from "repositories/branch"
import SelectDescription from "components/forms/select-description"
import Table from "components/table"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch, canFilterAllOutlets } = useAuth()
    const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const productUnit = searchParams.get('product_unit_id')
    const rawBranchId = searchParams.get("branch_id") || "ALL"
    const branchId = isBranchLocked ? String(currentBranch.id) : rawBranchId
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')

    // Branch selection
    const { data: branchSelection, isLoading: isLoadingBranches } = useBranchRepositories({ paginate: false })
    const branchOptions = useMemo(() => {
        const branches = Array.isArray(branchSelection) ? branchSelection : []
        if (isBranchLocked) return branches
        return [{ id: "ALL", name: "Semua Cabang" }, ...branches]
    }, [branchSelection, isBranchLocked])

    const { data: productUnitMapData, isLoading: isLoadingProductUnitMapData, mutate: mutateProductUnitMapData } = useProductUnitExclusivityRepositories({
        page,
        search,
        product_unit_id: productUnit,
        branch_id: isBranchLocked ? branchId : (branchId === "ALL" ? undefined : branchId),
        from,
        to,

        order_by: orderBy,
        direction
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingProductUnitMapData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [productUnitMapData, isLoadingProductUnitMapData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Branch filter handler (merge to preserve other params)
    const onChangeBranch = (value) => {
        const current = Object.fromEntries(searchParams.entries())
        const merged = { ...current, branch_id: value || "ALL", page: 1 }

        if (merged.branch_id === "ALL") {
            delete merged.branch_id
        }

        setSearchParams(merged)
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }

    // Data list props
    const dataListHeaders = [
        {
            isAction: false,
            content: "Unit Produk",
            sortKey: "product_unit.name"
        },
        {
            isAction: false,
            content: "Cabang",
            sortKey: "branch.name"
        },
        {
            isAction: false,
            content: "Dibuat Pada",
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: "Diubah Pada",
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        {
            type: "default",
            bold: false,
            content: (value) => (
                <div className="flex flex-col space-y-1">
                    <div>{value.product_unit?.name}</div>
                    <div className="font-medium text-neutral-900">{value.product_unit?.long_name}</div>
                </div>
            )
        },
        {
            type: "default",
            bold: true,
            content: (value) => value.branch?.name
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('product-unit-map.delete') && (
                        <Delete data={value} onSuccess={() => mutateProductUnitMapData()} />
                    )}
                </>
            )
        }
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }} value={searchTemp} type="text" placeholder={`${lang.search} Eksklusivitas Unit Produk`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ productUnit, branch_id: branchId === "ALL" ? null : branchId, from, to }).filter(([_, v]) => v != null))} />
                    <div className="w-48">
                        <SelectDescription
                            selection={branchOptions}
                            isLoading={isLoadingBranches}
                            value={branchId}
                            onChange={onChangeBranch}
                            disabled={isBranchLocked}
                            keyValue={(v) => v.id}
                            title={(v) => v.name}
                            description={(v) => v.code || v.id}
                            placeholder="Cabang"
                        />
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {can('product-unit-map.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            {/* <Table isLoading={isLoadingProductUnitMapData} data={productUnitMapData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={productUnitMapData?.data.length === 0 && !search && !isLoadingProductUnitMapData} isEmptyOnSearching={productUnitMapData?.data.length === 0 && search && !isLoadingProductUnitMapData} toggleSort={toggleSort} orderBy={orderBy} direction={direction} /> */}
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                    <thead className="bg-neutral-50 rounded-t-3xl">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Unit Produk</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Cabang</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.created_at}</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.updated_at}</th>
                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">{lang.action}</span></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                        {/* When loading */}
                        {isLoadingProductUnitMapData && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.loading_data}
                                </td>
                            </tr>
                        )}

                        {/* When there are no list available */}
                        {productUnitMapData?.data.length === 0 && !search && !isLoadingProductUnitMapData && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_data}
                                </td>
                            </tr>
                        )}

                        {/* When there are no list available on searching */}
                        {productUnitMapData?.data.length === 0 && search && !isLoadingProductUnitMapData && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_result}
                                </td>
                            </tr>
                        )}

                        {productUnitMapData?.data.length > 0 && productUnitMapData.data.map((row) => (
                            <tr key={row.id} className="hover:bg-brand-50 transition-colors">
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    <div className="flex flex-col space-y-1">
                                        <div>{row.product_unit?.name}</div>
                                        <div className="font-medium text-neutral-900">{row.product_unit?.long_name}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {row.branch?.name}
                                </td>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {moment(row.created_at).format('MMMM D, YYYY')}
                                </td>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {row.updated_at ? moment(row.updated_at).format('MMMM D, YYYY') : '-'}
                                </td>
                                <td class="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                    <div class="inline-flex items-center space-x-2">
                                        {/* {can('product-unit-map.update') && (
                                            <Link to={`${row.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90">
                                                <Pencil className="w-6 h-6" />
                                            </Link>
                                        )} */}

                                        {can('product-unit-map.delete') && (
                                            <Delete data={row} onSuccess={() => mutateProductUnitMapData()} />
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination links={productUnitMapData?.links} from={productUnitMapData?.from} to={productUnitMapData?.to} total={productUnitMapData?.total} />
        </div>
    )
}
